import { Route } from "../types";
import { checkLoggedIn } from "../services/cognito";
import { getApi } from "../services/api";
import { Pages } from "../pages/base";
import LoginPage from "../pages/login";
import RegisterPage from "../pages/register";
import HomePage from "../pages/home";
import RecipePage from "../pages/recipe";
import NotFoundPage from "../pages/notfound";
import PlanPage from "../pages/plan";
import ShoppingListPage from "../pages/shoppinglist";
import PantryPage from "../pages/pantry";
import PlanTemplatePage from "../pages/plantemplate";
import ProfilePage from "../pages/profile";
import HouseholdSetupPage from "../pages/householdsetup";

export class Router {
  private routes: Route[] = [];
  private defaultRoute: Route;
  private loginRoute: Route;
  private container: HTMLElement;

  constructor(containerEl: HTMLElement, defaultRoute: Route, loginRoute: Route) {
    this.container = containerEl;
    this.defaultRoute = defaultRoute;
    this.loginRoute = loginRoute;
    this.handlePopState = this.handlePopState.bind(this);
    window.addEventListener("popstate", this.handlePopState);
  }

  addRoute(route: Route): void {
    this.routes.push(route);
  }

  async navigate(path: string): Promise<void> {
    const route = this.routes.find((r) => r.path === path) || this.defaultRoute;

    if (route.protected) {
      const isLoggedIn = await checkLoggedIn();
      if (!isLoggedIn) {
        // Store the attempted path to redirect back after login
        sessionStorage.setItem("redirectPath", path);
        window.history.pushState({}, "", this.loginRoute.path);
        this.renderRoute(this.loginRoute);
        return;
      }
    }

    window.history.pushState({}, "", path);
    this.renderRoute(route);
  }

  private async handlePopState(): Promise<void> {
    await this.handleRoute(window.location.pathname);
  }

  private async navigationGuard(isLoggedIn: boolean, path: string): Promise<string> {
    const currentPath = path.replace(/\/$/, "");
    const allowedUnAuthPaths = ["/login", "/register", "/404"];
    const disabledAuthPaths = ["/login", "/register"];
    const householdSetupPath = "/household-setup";
    let newPath = path;

    if (currentPath === "/") {
      if (!isLoggedIn) {
        newPath = "/login";
      }
    }

    if (!isLoggedIn && !allowedUnAuthPaths.includes(currentPath)) {
      console.log(`not logged in and path is not an allowed unauth path. redirecting to /login.`);
      newPath = "/login";
    }

    // Check household assignment for authenticated users
    if (isLoggedIn &&
        currentPath !== householdSetupPath &&
        !allowedUnAuthPaths.includes(currentPath)) {

      const apiUrl = import.meta.env.VITE_API_URL as string;
      const apiToken = localStorage.getItem("idToken") as string;

      try {
        const userProfile = await getApi(apiUrl, "user", apiToken);

        // If no household, redirect to setup
        if (!userProfile?.household) {
          console.log("User has no household. Redirecting to setup.");
          newPath = householdSetupPath;
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
        // Fail open - allow navigation on error
      }
    }

    if (isLoggedIn && disabledAuthPaths.includes(currentPath)) {
      console.log(`logged in and path is in the disabled auth paths. redirecting to /.`);
      newPath = "/";
    }

    if (path != newPath) {
      window.location.href = newPath;
    }
    return newPath;
  }

  private async handleRoute(path: string): Promise<void> {
    const isLoggedIn = await checkLoggedIn();
    path = await this.navigationGuard(isLoggedIn, path);
    console.log(path);
    let route: Route;
    if (path != "/login") {
      route = this.routes.find((r) => r.path === path) || this.defaultRoute;
    } else {
      route = this.loginRoute;
    }

    if (route.protected) {
      if (!isLoggedIn) {
        sessionStorage.setItem("redirectPath", path);
        window.history.replaceState({}, "", this.loginRoute.path);
        this.renderRoute(this.loginRoute);
        return;
      }
    }

    this.renderRoute(route);
  }

  private renderRoute(route: Route): void {
    this.container.innerHTML = "";

    console.log(`Rendering route: ${route.pageName}`);

    switch (route.pageName) {
      case Pages.LOGIN:
        new LoginPage();
        break;
      case Pages.REGISTER:
        new RegisterPage();
        break;
      case Pages.HOME:
        new HomePage();
        break;
      case Pages.RECIPE:
        new RecipePage();
        break;
      case Pages.PLAN:
        new PlanPage();
        break;
      case Pages.SHOPPINGLIST:
        new ShoppingListPage();
        break;
      case Pages.PANTRY:
        new PantryPage();
        break;
      case Pages.PLANTEMPLATE:
        new PlanTemplatePage();
        break;
      case Pages.PROFILE:
        new ProfilePage();
        break;
      case Pages.HOUSEHOLD_SETUP:
        new HouseholdSetupPage();
        break;
      default:
        new NotFoundPage();
        break;
    }
  }

  /**
   * Call this after successful login to redirect to the originally requested page
   */
  redirectToSavedPath(): void {
    const redirectPath = sessionStorage.getItem("redirectPath");
    if (redirectPath) {
      sessionStorage.removeItem("redirectPath");
      this.navigate(redirectPath);
    } else {
      this.navigate("/");
    }
  }

  init(): void {
    this.handleRoute(window.location.pathname);
  }

  destroy(): void {
    window.removeEventListener("popstate", this.handlePopState);
  }
}
